import { useQuery } from '@apollo/client';
import { Partner } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Loading from '@platform-ui-common/components/Loading/index.js';
import Markdown from '@platform-ui-common/components/Markdown/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useForm, { ValidationMode } from '@platform-ui-common/hooks/useForm/index.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import Color from '@platform-ui-common/utilities/Color.js';
import replacePlaceholders from '@platform-ui-common/utilities/placeholder.js';
import { FullWidthPanel } from '@platform-ui/application/components/index.js';
import { IdentityPaths } from '@platform-ui/auth/Routes.js';
import EligibilityMessage from '@platform-ui/eligibility/components/EligibilityMessage/index.js';
import EligibilityQuestion from '@platform-ui/eligibility/components/EligibilityQuestion/index.js';
import GetPartnerEligibilityQuery from '@platform-ui/eligibility/graphql/GetPartnerEligibilityQuery.graphql';
import { DEFAULT_COPY } from '@platform-ui/eligibility/utils/constants.js';
import { checkEligibility, getConfigFormInit } from '@platform-ui/eligibility/utils/index.js';
import { useUserHomeRoute } from '@platform-ui/utilities/Routes.js';
import { useState } from 'react';
import { Navigate } from 'react-router-dom-v5-compat';

export default function CheckEligibility(): JSX.Element {
  const partner = usePartner();
  const home = useUserHomeRoute();
  const { data, loading, error } = useQuery<
    { partners: { partners: Partner[] } },
    { externalId: string }
  >(GetPartnerEligibilityQuery, { variables: { externalId: partner.externalId } });

  const config = data?.partners?.partners?.[0]?.eligibility;

  const [isEligible, setIsEligible] = useState(undefined);
  const placeholderSource = usePlaceholder();

  const { defaults, validation } = getConfigFormInit(config);
  const {
    formData,
    errors,
    trySubmit,
    dispatch,
    counters: { missingRequiredFields },
  } = useForm(defaults, validation, ValidationMode.RequiredOnSubmit);

  const onSubmit = (): void => {
    trySubmit((): void => {
      setIsEligible(checkEligibility(config, formData));
    });
  };

  if (loading) return <Loading size="XXL" fullPage />;
  if (error)
    return (
      <Typography variant="body" color={Color.Error}>
        Uh oh! An error occurred.
      </Typography>
    );
  if (!config) return <Navigate to={home} />;
  return (
    <FullWidthPanel>
      <FormContainer
        title={
          config?.title ? replacePlaceholders(config.title, placeholderSource) : DEFAULT_COPY.title
        }
        buttons={
          isEligible ? (
            <Button link={IdentityPaths.SIGNIN}>Create Your Free Account</Button>
          ) : (
            <div className="flex flex-col gap-4 items-center">
              <ErrorDisplay errors={missingRequiredFields} visible={missingRequiredFields > 0} />
              <Button onClick={onSubmit}>
                {isEligible === undefined ? 'Check Eligibility' : 'Recheck Eligibility'}
              </Button>
            </div>
          )
        }
      >
        <div className="max-w-[44rem] px-7">
          {config?.copy ? (
            <Markdown variant="largeBody" alignment="center">
              {replacePlaceholders(config.copy, placeholderSource)}
            </Markdown>
          ) : (
            DEFAULT_COPY.copy
          )}
        </div>
        <div
          className="bg-lightBackground border-tableBackground rounded-md flex flex-col 
                        w-full gap-4 items-center max-w-[36rem] py-2 px-7 lg:px-12"
        >
          {config?.questions?.map((question) => (
            <EligibilityQuestion
              key={question.id}
              question={question}
              formData={formData}
              dispatch={dispatch}
              errors={errors}
              placeholders={placeholderSource}
            />
          ))}
          <EligibilityMessage eligible={isEligible} partner={partner} config={config} />
        </div>
      </FormContainer>
    </FullWidthPanel>
  );
}
